import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { relayApi, type TraceEntry, type ChallengeData } from '@/api/relay';
import { useAuthStore } from '@/store/authStore';
import {
  Radio, Copy, Check, Download, AlertTriangle,
  ArrowDownLeft, ArrowUpRight, ExternalLink,
  ChevronDown, PiggyBank, Zap, Square, Layers, Share2, Clock, RefreshCw,
} from 'lucide-react';
import { SwipaSend as Send } from '@/icons/SwipaSend';

const _UNIT = 'SWIPA';
const _SUB = 100_000; // 1 SWIPA = 100,000 sub-units

function fmtSwipa(sub: number): string {
  const whole = Math.floor(sub / _SUB);
  const frac = sub % _SUB;
  if (frac === 0) return whole.toString();
  const fracStr = frac.toString().padStart(5, '0').replace(/0+$/, '');
  return `${whole}.${fracStr}`;
}

function parseSwipa(input: string): number {
  const n = parseFloat(input);
  if (isNaN(n) || n <= 0) return 0;
  return Math.round(n * _SUB);
}

function TraceRow({ t }: { t: TraceEntry }) {
  const isIn = t.dir === 'in';
  const isEmit = t.dir === 'emit';
  const isPending = t.status === 'pending';
  const date = new Date(t.createdAt);
  const time = `${date.toLocaleDateString('ru-RU')} ${date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <div className={`flex items-center gap-3 py-2 border-b border-surface-800 last:border-0 ${isPending ? 'opacity-60' : ''}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
        isPending ? 'bg-surface-700' : isEmit ? 'bg-amber-900/40' : isIn ? 'bg-green-900/40' : 'bg-red-900/40'
      }`}>
        {isPending ? <Clock size={13} className="text-surface-400 animate-pulse" />
          : isEmit ? <ExternalLink size={13} className="text-amber-400" />
          : isIn ? <ArrowDownLeft size={13} className="text-green-400" />
          : <ArrowUpRight size={13} className="text-red-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-surface-200 text-sm">
          {isEmit ? 'Вывод' : isIn ? (t.peer ? t.peer.displayName : 'Начисление') : t.peer?.displayName ?? '—'}
          {isPending && <span className="text-surface-500 text-xs ml-1.5">pending</span>}
        </p>
        {t.memo && <p className="text-surface-500 text-xs truncate">{t.memo}</p>}
      </div>
      <div className="text-right flex-shrink-0">
        <p className={`text-sm font-medium ${isPending ? 'text-surface-400' : isIn ? 'text-green-400' : 'text-red-400'}`}>
          {isIn ? '+' : '−'}{fmtSwipa(t.amount)} {_UNIT}
        </p>
        <p className="text-surface-600 text-[10px]">{time}</p>
      </div>
    </div>
  );
}

function SendForm({ onDone }: { onDone: () => void }) {
  const [target, setTarget] = useState('');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => relayApi.dispatch(target.trim(), parseSwipa(amount), memo.trim() || undefined),
    onSuccess: () => {
      setTarget('');
      setAmount('');
      setMemo('');
      setError(null);
      onDone();
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Ошибка отправки');
    },
  });

  return (
    <div className="space-y-2">
      {error && (
        <div className="bg-red-900/30 border border-red-800/50 text-red-300 text-xs rounded-lg px-3 py-2">{error}</div>
      )}
      <input
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        placeholder="Имя пользователя"
        className="irc-input text-sm"
      />
      <div className="flex gap-2">
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="Количество"
          inputMode="numeric"
          className="irc-input text-sm flex-1"
        />
        <span className="flex items-center text-surface-500 text-xs px-2">{_UNIT}</span>
      </div>
      <input
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        placeholder="Комментарий (необязательно)"
        maxLength={128}
        className="irc-input text-sm"
      />
      <button
        onClick={() => mut.mutate()}
        disabled={!target.trim() || !amount || parseSwipa(amount) <= 0 || mut.isPending}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        <Send size={14} />
        {mut.isPending ? 'Отправка...' : 'Отправить'}
      </button>
    </div>
  );
}

export default function RelayPanel() {
  const [copied, setCopied] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showDistribute, setShowDistribute] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [fragment, setFragment] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const { data: status, isLoading: statusLoading, isError: statusError } = useQuery({
    queryKey: ['relay-status'],
    queryFn: () => relayApi.status(),
    retry: false,
  });

  const { data: flux, refetch: refetchFlux } = useQuery({
    queryKey: ['relay-flux'],
    queryFn: () => relayApi.flux(),
    enabled: !!status?.active,
    retry: false,
  });

  const provisionMut = useMutation({
    mutationFn: () => relayApi.provision(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['relay-status'] });
      qc.invalidateQueries({ queryKey: ['relay-flux'] });
    },
  });

  const handleCopy = () => {
    if (!status?.node) return;
    navigator.clipboard.writeText(status.node);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleExport = async () => {
    const res = await relayApi.extract();
    setFragment(res.fragment);
    setShowExport(true);
  };

  if (statusLoading || statusError || !status) return null;

  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold mb-3 flex items-center gap-2">
        <Radio size={16} className="text-brand-400" />
        Relay Node
      </h3>

      {!status.active ? (
        <>
          <p className="text-surface-500 text-xs mb-4">
            Децентрализованный узел привязан к вашему аккаунту.
          </p>
          <button
            onClick={() => provisionMut.mutate()}
            disabled={provisionMut.isPending}
            className="btn-primary"
          >
            {provisionMut.isPending ? 'Инициализация...' : 'Активировать узел'}
          </button>
        </>
      ) : (
        <div className="space-y-4">
          {/* Balance */}
          <div className="bg-surface-800 border border-surface-700 rounded-xl p-4 text-center">
            <p className="text-surface-500 text-xs mb-1">Баланс</p>
            <p className="text-3xl font-bold text-surface-100">
              {fmtSwipa(flux?.capacity ?? 0)}
              <span className="text-base font-normal text-surface-400 ml-2">{_UNIT}</span>
            </p>
            {(flux?.pending ?? 0) > 0 && (
              <p className="text-surface-500 text-xs mt-1">
                <Clock size={10} className="inline mr-1" />
                {fmtSwipa(flux!.pending)} pending · доступно {fmtSwipa(flux!.capacity - flux!.pending)}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => { setShowSend(!showSend); setShowHistory(false); setShowDistribute(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                showSend
                  ? 'bg-brand-600 border-brand-500 text-white'
                  : 'bg-surface-800 border-surface-700 text-surface-300 hover:border-surface-600'
              }`}
            >
              <Send size={14} />
              Отправить
            </button>
            <button
              onClick={() => { setShowDistribute(!showDistribute); setShowSend(false); setShowHistory(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                showDistribute
                  ? 'bg-brand-600 border-brand-500 text-white'
                  : 'bg-surface-800 border-surface-700 text-surface-300 hover:border-surface-600'
              }`}
            >
              <Share2 size={14} />
              Раздать
            </button>
            <button
              onClick={() => { setShowHistory(!showHistory); setShowSend(false); setShowDistribute(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                showHistory
                  ? 'bg-brand-600 border-brand-500 text-white'
                  : 'bg-surface-800 border-surface-700 text-surface-300 hover:border-surface-600'
              }`}
            >
              <ChevronDown size={14} />
              История
            </button>
          </div>

          {/* Send form */}
          {showSend && (
            <SendForm onDone={() => {
              refetchFlux();
              setShowSend(false);
            }} />
          )}

          {/* Distribute form */}
          {showDistribute && (
            <DistributeForm onDone={() => {
              refetchFlux();
              setShowDistribute(false);
            }} />
          )}

          {/* History */}
          {showHistory && flux?.traces && (
            <div>
              {flux.traces.length === 0 ? (
                <p className="text-surface-500 text-xs text-center py-3">Нет транзакций</p>
              ) : (
                flux.traces.map((t) => <TraceRow key={t.id} t={t} />)
              )}
            </div>
          )}

          {/* Mining */}
          <MinerSection onMined={() => refetchFlux()} />

          {/* Node info (collapsed) */}
          <details className="group">
            <summary className="text-surface-500 text-xs cursor-pointer hover:text-surface-300 transition-colors">
              ID узла
            </summary>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-3 py-2">
                <code className="text-xs font-mono text-surface-200 break-all flex-1">
                  {status.node!.slice(0, 16)}...{status.node!.slice(-16)}
                </code>
                <button onClick={handleCopy} className="text-surface-400 hover:text-brand-400 flex-shrink-0">
                  {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                </button>
              </div>

              {!showExport ? (
                <button
                  onClick={handleExport}
                  className="flex items-center gap-1.5 text-xs text-surface-400 hover:text-surface-200 transition-colors"
                >
                  <Download size={13} />
                  Экспорт приватного фрагмента
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="bg-amber-900/30 border border-amber-800/50 text-amber-300 text-xs rounded-lg px-3 py-2 flex items-start gap-2">
                    <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>Никому не передавайте. Полный контроль над узлом.</span>
                  </div>
                  <div className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2">
                    <code className="text-xs font-mono text-red-300 break-all">{fragment}</code>
                  </div>
                  <button
                    onClick={() => { setShowExport(false); setFragment(null); }}
                    className="text-xs text-surface-500 hover:text-surface-300"
                  >
                    Скрыть
                  </button>
                </div>
              )}
            </div>
          </details>

          {/* Admin credit (only for SUPER_ADMIN) */}
          {user?.globalRole === 'SUPER_ADMIN' && <AdminCredit onDone={() => refetchFlux()} />}
        </div>
      )}
    </section>
  );
}

function DistributeForm({ onDone }: { onDone: () => void }) {
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: preview } = useQuery({
    queryKey: ['relay-distribute-preview'],
    queryFn: () => relayApi.distributePreview(),
  });

  const mut = useMutation({
    mutationFn: () => relayApi.distribute(parseSwipa(amount), memo.trim() || undefined),
    onSuccess: (data) => {
      setAmount('');
      setMemo('');
      setError(null);
      onDone();
      alert(`Раздано ${fmtSwipa(data.actualTotal)} ${_UNIT} → ${data.recipients} пользователям (${fmtSwipa(data.perUser)} каждому). Ожидает подтверждения в блоке.`);
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Ошибка');
    },
  });

  const subAmount = parseSwipa(amount);
  const perUser = preview?.recipients && subAmount > 0
    ? Math.floor(subAmount / preview.recipients)
    : 0;

  return (
    <div className="space-y-2">
      {error && (
        <div className="bg-red-900/30 border border-red-800/50 text-red-300 text-xs rounded-lg px-3 py-2">{error}</div>
      )}
      {preview && (
        <div className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-xs text-surface-400">
          Получателей: <span className="text-surface-200 font-medium">{preview.recipients}</span>
          {perUser > 0 && <> · ~<span className="text-surface-200">{fmtSwipa(perUser)}</span> {_UNIT} каждому</>}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="Общая сумма"
          inputMode="numeric"
          className="irc-input text-sm flex-1"
        />
        <span className="flex items-center text-surface-500 text-xs px-2">{_UNIT}</span>
      </div>
      <input
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        placeholder="Комментарий (необязательно)"
        maxLength={128}
        className="irc-input text-sm"
      />
      <button
        onClick={() => mut.mutate()}
        disabled={!amount || subAmount <= 0 || perUser < 1 || mut.isPending}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        <Share2 size={14} />
        {mut.isPending ? 'Раздача...' : 'Распространить'}
      </button>
    </div>
  );
}

// ── Копилка SWIPA (internal: proof-of-work accumulator) ─────────────────────
// User-facing framing: «копилка» — пассивное накопление SWIPA за активность.
// Внутри реализовано как PoW, но ни одного слова о mining/blockchain юзер
// не видит.  См. claude agent diary rule: никогда публично про крипто/майнинг.

interface MinerLog {
  id: number;
  time: string;
  text: string;
  type: 'block' | 'diff' | 'info' | 'error';
}

let _logId = 0;
function mkLog(text: string, type: MinerLog['type'] = 'info'): MinerLog {
  const now = new Date();
  return {
    id: ++_logId,
    time: now.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    text,
    type,
  };
}

const NONCE_STRIDE = 1_000_000_000;
const MAX_THREADS = typeof navigator !== 'undefined' ? Math.min(navigator.hardwareConcurrency || 2, 8) : 2;

function MinerSection({ onMined }: { onMined: () => void }) {
  const workersRef = useRef<Worker[]>([]);
  const ratesRef = useRef<Map<number, number>>(new Map());
  const logRef = useRef<HTMLDivElement | null>(null);
  const prevDiffRef = useRef<number | null>(null);
  const sealingRef = useRef(false);
  const [mining, setMining] = useState(false);
  const [threads, setThreads] = useState(Math.min(2, MAX_THREADS));
  const [hashrate, setHashrate] = useState(0);
  const [found, setFound] = useState(0);
  const [earned, setEarned] = useState(0);
  const [logs, setLogs] = useState<MinerLog[]>([]);
  const [error, setError] = useState<string | null>(null);

  const pushLog = useCallback((text: string, type: MinerLog['type'] = 'info') => {
    setLogs((prev) => [...prev.slice(-49), mkLog(text, type)]);
    setTimeout(() => logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' }), 50);
  }, []);

  const { data: chain, refetch: refetchChain } = useQuery({
    queryKey: ['relay-chain'],
    queryFn: () => relayApi.chain(),
    refetchInterval: mining ? 8000 : 30000,
  });

  useEffect(() => {
    if (!chain || !mining) return;
    if (prevDiffRef.current !== null && chain.diff !== prevDiffRef.current) {
      const dir = chain.diff > prevDiffRef.current ? 'выше' : 'ниже';
      pushLog(`Нагрузка ${dir}: ${prevDiffRef.current} → ${chain.diff}`, 'diff');
    }
    prevDiffRef.current = chain.diff;
  }, [chain?.diff, mining]);

  const broadcastChallenge = useCallback((ch: ChallengeData) => {
    workersRef.current.forEach((w, i) => {
      w.postMessage({ type: 'start', challenge: ch, nonceOffset: i * NONCE_STRIDE });
    });
  }, []);

  const sealMut = useMutation({
    mutationFn: ({ nonce, hash, extranonce1, extranonce2, timestamp, nonceUint32 }: {
      nonce: string; hash: string;
      extranonce1?: string; extranonce2?: string; timestamp?: number; nonceUint32?: number;
    }) => {
      const hd = extranonce1 != null ? { extranonce1: extranonce1!, extranonce2: extranonce2!, timestamp: timestamp!, nonceUint32: nonceUint32! } : undefined;
      return relayApi.seal(nonce, hash, hd);
    },
    onSuccess: (data) => {
      sealingRef.current = false;
      setFound((p) => p + 1);
      setEarned((p) => p + data.reward);
      setError(null);
      pushLog(`Раунд #${data.idx} засчитан! +${fmtSwipa(data.reward)} ${_UNIT}${data.txCount ? ` (${data.txCount} операций)` : ''}`, 'block');
      refetchChain();
      onMined();
      startNewChallenge();
    },
    onError: (e: unknown) => {
      sealingRef.current = false;
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      pushLog(`Отклонено: ${msg ?? 'ошибка'}`, msg === 'Раунд уже закрыт' || msg === 'Неверный результат' ? 'info' : 'error');
      // always continue mining with fresh challenge
      startNewChallenge();
    },
  });

  const startNewChallenge = useCallback(async () => {
    try {
      const ch = await relayApi.challenge();
      if (!ch) { pushLog('Нет активного раунда', 'error'); stopMining(); return; }
      pushLog(`Раунд #${ch.idx} · нагрузка ${ch.diff} · бонус ${fmtSwipa(ch.reward)}${ch.txCount ? ` · ${ch.txCount} операций` : ''}`);
      broadcastChallenge(ch);
    } catch {
      pushLog('Не удалось подключиться к копилке', 'error');
      stopMining();
    }
  }, []);

  const startMining = useCallback(async () => {
    setError(null);
    sealingRef.current = false;
    try {
      const ch = await relayApi.challenge();
      if (!ch) { setError('Node not ready'); return; }

      const workers: Worker[] = [];
      for (let i = 0; i < threads; i++) {
        const w = new Worker(
          new URL('@/lib/miner.worker.ts', import.meta.url),
          { type: 'module' },
        );
        const idx = i;
        w.onmessage = (e) => {
          const { type, rate, nonce, hash } = e.data;
          if (type === 'hashrate') {
            ratesRef.current.set(idx, rate);
            let total = 0;
            ratesRef.current.forEach((v) => { total += v; });
            setHashrate(total);
          }
          if (type === 'solved' && !sealingRef.current) {
            sealingRef.current = true;
            const { extranonce1, extranonce2, timestamp, nonceUint32 } = e.data;
            sealMut.mutate({ nonce, hash, extranonce1, extranonce2, timestamp, nonceUint32 });
          }
        };
        workers.push(w);
      }

      workersRef.current = workers;
      ratesRef.current.clear();
      prevDiffRef.current = ch.diff;
      setLogs([]);
      pushLog(`Копилка работает (${threads} поток${threads === 1 ? '' : threads < 5 ? 'а' : 'ов'})`);
      pushLog(`Раунд #${ch.idx} · нагрузка ${ch.diff} · бонус ${fmtSwipa(ch.reward)}${ch.txCount ? ` · ${ch.txCount} операций` : ''}`);
      workers.forEach((w, i) => {
        w.postMessage({ type: 'start', challenge: ch, nonceOffset: i * NONCE_STRIDE });
      });
      setMining(true);
    } catch {
      setError('Failed to start');
    }
  }, [threads]);

  const stopMining = useCallback(() => {
    workersRef.current.forEach((w) => { w.postMessage({ type: 'stop' }); w.terminate(); });
    workersRef.current = [];
    ratesRef.current.clear();
    setMining(false);
    setHashrate(0);
    pushLog('Копилка на паузе');
  }, []);

  useEffect(() => {
    return () => { workersRef.current.forEach((w) => w.terminate()); };
  }, []);

  const fmtRate = (r: number) => {
    if (r >= 1000000) return `${(r / 1000000).toFixed(1)} MH/s`;
    if (r >= 1000) return `${(r / 1000).toFixed(1)} kH/s`;
    return `${r} H/s`;
  };

  const logColor: Record<MinerLog['type'], string> = {
    block: 'text-green-400',
    diff: 'text-amber-400',
    info: 'text-surface-400',
    error: 'text-red-400',
  };

  return (
    <div className="space-y-3">
      {/* Chain info */}
      {chain && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-surface-800 rounded-lg px-2 py-2">
            <p className="text-surface-500 text-[10px]">Раунд</p>
            <p className="text-surface-200 text-sm font-medium">{chain.height}</p>
          </div>
          <div className="bg-surface-800 rounded-lg px-2 py-2">
            <p className="text-surface-500 text-[10px]">Нагрузка</p>
            <p className="text-surface-200 text-sm font-medium">{chain.diff}</p>
          </div>
          <div className="bg-surface-800 rounded-lg px-2 py-2">
            <p className="text-surface-500 text-[10px]">Бонус</p>
            <p className="text-surface-200 text-sm font-medium">{fmtSwipa(chain.reward)}</p>
          </div>
        </div>
      )}

      {/* Thread selector + start/stop */}
      {!mining ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <label className="text-surface-400 text-xs flex-shrink-0">Потоки</label>
            <input
              type="range"
              min={1}
              max={MAX_THREADS}
              value={threads}
              onChange={(e) => setThreads(Number(e.target.value))}
              className="flex-1 accent-amber-500"
            />
            <span className="text-surface-200 text-sm font-mono w-6 text-right">{threads}</span>
          </div>
          <button
            onClick={startMining}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium text-sm transition-colors"
          >
            <PiggyBank size={15} />
            Копить SWIPA
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="bg-amber-900/20 border border-amber-800/40 rounded-lg px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-amber-300 text-sm font-medium">
                  Копилка ({threads} поток{threads === 1 ? '' : threads < 5 ? 'а' : 'ов'})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { stopMining(); setTimeout(startMining, 300); }}
                  className="flex items-center gap-1 text-xs text-surface-400 hover:text-amber-400 transition-colors"
                  title="Перезапуск"
                >
                  <RefreshCw size={12} />
                </button>
                <button
                  onClick={stopMining}
                  className="flex items-center gap-1 text-xs text-surface-400 hover:text-red-400 transition-colors"
                >
                  <Square size={12} />
                  Стоп
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Zap size={13} className="text-amber-400" />
                <span className="text-surface-200 text-sm font-mono">{fmtRate(hashrate)}</span>
              </div>
              {found > 0 && (
                <div className="flex items-center gap-1.5">
                  <Layers size={13} className="text-green-400" />
                  <span className="text-green-400 text-sm">{found} раунд{found === 1 ? '' : found < 5 ? 'а' : 'ов'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Session earnings */}
          {earned > 0 && (
            <div className="bg-green-900/20 border border-green-800/40 rounded-lg px-4 py-2.5 flex items-center justify-between">
              <span className="text-green-300 text-xs">Заработано за сессию</span>
              <span className="text-green-400 text-sm font-bold">+{fmtSwipa(earned)} {_UNIT}</span>
            </div>
          )}
        </div>
      )}

      {/* Event log */}
      {logs.length > 0 && (
        <div
          ref={logRef}
          className="bg-surface-950 border border-surface-800 rounded-lg p-2 max-h-36 overflow-y-auto scrollbar-thin"
        >
          {logs.map((l) => (
            <div key={l.id} className="flex gap-2 py-0.5">
              <span className="text-surface-600 text-[10px] font-mono flex-shrink-0 leading-4">{l.time}</span>
              <span className={`text-[11px] leading-4 ${logColor[l.type]}`}>{l.text}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-900/30 border border-red-800/50 text-red-300 text-xs rounded-lg px-3 py-2">{error}</div>
      )}
    </div>
  );
}

function AdminCredit({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => relayApi.credit(target.trim(), parseSwipa(amount), memo.trim() || undefined),
    onSuccess: () => {
      setTarget('');
      setAmount('');
      setMemo('');
      setError(null);
      setOpen(false);
      onDone();
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Ошибка');
    },
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-xs text-amber-500 hover:text-amber-400 transition-colors">
        Начислить (admin)
      </button>
    );
  }

  return (
    <div className="bg-amber-900/10 border border-amber-900/30 rounded-lg p-3 space-y-2">
      <p className="text-amber-400 text-xs font-medium">Начисление {_UNIT}</p>
      {error && (
        <div className="bg-red-900/30 border border-red-800/50 text-red-300 text-xs rounded-lg px-3 py-2">{error}</div>
      )}
      <input
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        placeholder="Username получателя"
        className="irc-input text-sm"
      />
      <input
        value={amount}
        onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
        placeholder="Количество"
        inputMode="numeric"
        className="irc-input text-sm"
      />
      <input
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        placeholder="Причина"
        maxLength={128}
        className="irc-input text-sm"
      />
      <div className="flex gap-2">
        <button
          onClick={() => mut.mutate()}
          disabled={!target.trim() || !amount || mut.isPending}
          className="btn-primary flex-1 text-sm"
        >
          {mut.isPending ? '...' : 'Начислить'}
        </button>
        <button onClick={() => { setOpen(false); setError(null); }} className="btn-ghost text-sm">
          Отмена
        </button>
      </div>
    </div>
  );
}
